import { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Shuffle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/Dialog'
import { InputField } from '@/components/shared/form'

export interface CreateCodeValues {
  code: string
  reward: string
  limit: number
  expires: string
}

const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'

function randomCode() {
  return Array.from(
    { length: 10 },
    () => ALPHABET[Math.floor(Math.random() * ALPHABET.length)],
  ).join('')
}

const EMPTY = { code: '', reward: '', limit: 1000, expires: '2026-12-31' }

interface CreateCodeDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreate: (values: CreateCodeValues) => void
}

export function CreateCodeDialog({ open, onOpenChange, onCreate }: CreateCodeDialogProps) {
  /* Inertia's useForm, not react-hook-form: this is what the rest of the app
   * uses, and it means wiring a real `form.post('/gift_codes')` later is one
   * line rather than a rewrite. Validation is inline for now because there is
   * no controller yet — once there is, Rails returns form.errors and these
   * checks come out. */
  const form = useForm<CreateCodeValues>({ ...EMPTY })

  useEffect(() => {
    if (open) {
      form.setData({ ...EMPTY })
      form.clearErrors()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    form.clearErrors()

    const errors: Partial<Record<keyof CreateCodeValues, string>> = {}
    if (form.data.code.trim().length < 4) errors.code = 'At least 4 characters.'
    else if (!/^[A-Z0-9]+$/.test(form.data.code)) errors.code = 'Uppercase letters and digits only.'
    if (!form.data.reward.trim()) errors.reward = 'Describe what the code grants.'
    if (!(Number(form.data.limit) >= 1)) errors.limit = 'At least one redemption.'
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.data.expires)) errors.expires = 'Use YYYY-MM-DD.'

    if (Object.keys(errors).length > 0) {
      form.setError(errors)
      return
    }

    onCreate({ ...form.data, limit: Number(form.data.limit) })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} className="w-[460px]">
      <DialogHeader>
        <DialogTitle>New gift code</DialogTitle>
      </DialogHeader>

      <DialogBody>
        <form id="code-form" onSubmit={submit} noValidate className="flex flex-col gap-3">
          <InputField
            form={form}
            name="code"
            label="Code"
            placeholder="GOLDRUSH26"
            required
            className="uppercase"
            labelExtra={
              <button
                type="button"
                onClick={() => form.setData('code', randomCode())}
                className="text-accent flex items-center gap-1 text-xs hover:underline"
              >
                <Shuffle className="size-3" />
                Generate
              </button>
            }
          />
          <InputField form={form} name="reward" label="Reward" placeholder="500 gems" required />
          <div className="grid grid-cols-2 gap-2">
            <InputField form={form} name="limit" label="Max redemptions" inputMode="numeric" />
            <InputField form={form} name="expires" label="Expires" placeholder="2026-12-31" />
          </div>
        </form>
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button type="submit" form="code-form">
          Create Code
        </Button>
      </DialogFooter>
    </Dialog>
  )
}
